import React, { useEffect, useState } from "react";
import { Typography, Radio, Textarea } from "@material-tailwind/react";

export interface PreMeetingReportData {
  lawyerFeedback: {
    arrivedOnTime?: boolean | null;
    receivedDefenseCopy?: boolean | null;
    receivedProcessGuidance?: boolean | null;
    receivedTestimonyGuidance?: boolean | null;
    preMeetingRating?: number;
    optionalNotes?: string;
  };
}

interface Props {
  onChange: (data: { isValid: boolean; formattedData: PreMeetingReportData }) => void;
}

type YesNoNa = "Sim" | "Não" | "Não se aplica" | null;

const toBooleanOrNull = (value: YesNoNa): boolean | null | undefined => {
  if (value === "Sim") return true;
  if (value === "Não") return false;
  if (value === "Não se aplica") return null;
  return undefined;
};

const PreMeetingReport: React.FC<Props> = ({ onChange }) => {
  const [form, setForm] = useState<{
    arrivedOnTime: YesNoNa;
    receivedDefenseCopy: YesNoNa;
    receivedProcessGuidance: YesNoNa;
    receivedTestimonyGuidance: YesNoNa;
    preMeetingRating: string;
    optionalNotes: string;
  }>({
    arrivedOnTime: null,
    receivedDefenseCopy: null,
    receivedProcessGuidance: null,
    receivedTestimonyGuidance: null,
    preMeetingRating: "",
    optionalNotes: "",
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isValid, setIsValid] = useState(false);

  const required = [
    "arrivedOnTime",
    "receivedDefenseCopy",
    "receivedProcessGuidance",
    "receivedTestimonyGuidance",
    "preMeetingRating",
  ];

  const ratingBelowSeven =
    form.preMeetingRating !== "" && Number(form.preMeetingRating) < 7;

  useEffect(() => {
    const ratingNum = Number(form.preMeetingRating);
    const isRatingValid = form.preMeetingRating !== "" && !isNaN(ratingNum) && Number.isInteger(ratingNum) && ratingNum >= 1 && ratingNum <= 10;
    const ok = required.every((k) => {
      if (k === "preMeetingRating") return isRatingValid;
      return !!(form as any)[k];
    });
    // Nota abaixo de 7 exige o preenchimento do motivo
    const notesRequired = ratingBelowSeven && form.optionalNotes.trim() === "";
    setIsValid(ok && !notesRequired);
  }, [form]);

  useEffect(() => {
    onChange({
      isValid,
      formattedData: {
        lawyerFeedback: {
          arrivedOnTime: toBooleanOrNull(form.arrivedOnTime),
          receivedDefenseCopy: toBooleanOrNull(form.receivedDefenseCopy),
          receivedProcessGuidance: toBooleanOrNull(form.receivedProcessGuidance),
          receivedTestimonyGuidance: toBooleanOrNull(form.receivedTestimonyGuidance),
          preMeetingRating: form.preMeetingRating === "" ? undefined : Number(form.preMeetingRating),
          optionalNotes: form.optionalNotes?.trim() || undefined,
        },
      },
    });
  }, [form, isValid, onChange]);

  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));
  const isInvalid = (name: string) => {
    if (name === "preMeetingRating") {
      if (!touched[name]) return false;
      const ratingNum = Number(form.preMeetingRating);
      return form.preMeetingRating === "" || isNaN(ratingNum) || !Number.isInteger(ratingNum) || ratingNum < 1 || ratingNum > 10;
    }
    return touched[name] && !(form as any)[name];
  };

  const yesNoField = (
    name: keyof typeof form,
    label: string,
  ) => (
    <div>
      <Typography variant="small" className="text-blue-700 font-medium mb-1">
        {label} <span className="text-red-500">*</span>
      </Typography>
      <div className="flex gap-4 flex-wrap">
        {(["Sim", "Não", "Não se aplica"] as const).map((option) => (
          <Radio
            key={option}
            name={name}
            label={option}
            checked={(form as any)[name] === option}
            onChange={() => {
              markTouched(String(name));
              setForm((s) => ({ ...s, [name]: option }));
            }}
          />
        ))}
      </div>
      {isInvalid(String(name)) && (
        <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
      )}
    </div>
  );

  const notesInvalid =
    ratingBelowSeven && touched.optionalNotes && form.optionalNotes.trim() === "";

  return (
    <div className="space-y-4">
      {yesNoField("arrivedOnTime", "O advogado chegou no horário para a reunião prévia?")}
      {yesNoField("receivedDefenseCopy", "Você recebeu documentação sobre o caso (defesa, petições, etc.)?")}
      {yesNoField("receivedProcessGuidance", "Você recebeu orientações claras sobre o processo?")}
      {yesNoField("receivedTestimonyGuidance", "Você recebeu orientações sobre como se manifestar na audiência?")}

      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Como você se sentiu preparado após a reunião prévia? (1 a 10) <span className="text-red-500">*</span>
        </Typography>
        <Typography variant="small" className="text-gray-600 mb-2">
          1 = Despreparado | 10 = Muito preparado
        </Typography>
        <div className="flex gap-2 flex-wrap">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <Radio
              key={n}
              name="preMeetingRating"
              label={`${n}`}
              checked={form.preMeetingRating === String(n)}
              onChange={() => {
                markTouched("preMeetingRating");
                setForm((s) => ({ ...s, preMeetingRating: String(n) }));
              }}
            />
          ))}
        </div>
        {isInvalid("preMeetingRating") && (
          <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
        )}
      </div>

      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          {ratingBelowSeven ? (
            <>
              Descreva o motivo da nota abaixo de 7 <span className="text-red-500">*</span>
            </>
          ) : (
            "Observações adicionais (opcional)"
          )}
        </Typography>
        <Textarea
          name="optionalNotes"
          value={form.optionalNotes}
          onChange={(e) => {
            markTouched("optionalNotes");
            setForm((s) => ({ ...s, optionalNotes: e.target.value }));
          }}
          className={`w-full ${notesInvalid ? "border-red-500" : ""}`}
          placeholder={
            ratingBelowSeven
              ? "Explique os motivos da nota atribuída"
              : "Compartilhe suas observações sobre a reunião prévia"
          }
        />
        {notesInvalid && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório para notas abaixo de 7
          </Typography>
        )}
      </div>
    </div>
  );
};

export default PreMeetingReport;
